import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Clock, Lock, FileSearch, Scale } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

export function Landing() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-px mx-auto max-w-7xl pt-16 pb-24 sm:pt-24 sm:pb-32">
          <div className="max-w-3xl">
            <Badge variant="muted" className="mb-6 gap-1.5">
              <Sparkles className="h-3 w-3 text-accent" />
              IA jurídica em português
            </Badge>
            <h1 className="font-serif text-display-lg sm:text-display-xl lg:text-display-2xl text-balance">
              Seu contrato, revisado por <span className="text-gold-gradient">advogados de verdade</span> — em horas, não semanas.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl text-pretty">
              Suba seu contrato, receba uma pré-análise por IA destacando riscos e cláusulas críticas, e contrate um advogado especialista pelo preço que ele mesmo define. Sem agência, sem hora cheia.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild size="lg" variant="accent" className="gap-2">
                <Link to="/cadastro">
                  Começar grátis
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/como-funciona">Como funciona</Link>
              </Button>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              Sem mensalidade. Você só paga pela revisão que contratar — diretamente ao advogado, com escrow.
            </p>
          </div>
        </div>

        {/* Decorative gradient */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[600px]"
          style={{
            background:
              'radial-gradient(60% 60% at 70% 0%, hsl(46 65% 52% / 0.08), transparent 70%)',
          }}
        />
      </section>

      {/* Value props */}
      <section className="border-y border-border/60 bg-surface/40">
        <div className="container-px mx-auto max-w-7xl py-16">
          <h2 className="font-serif text-3xl mb-12 text-balance max-w-2xl">
            Por que founders, PJs e freelancers escolhem o Parecer.ai
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Sparkles,
                title: 'Pré-análise IA imediata',
                body: 'Claude Sonnet 4 lê seu contrato e destaca cláusulas de risco antes mesmo de você falar com um advogado.',
              },
              {
                icon: Scale,
                title: 'Advogados verificados',
                body: 'OAB ativa, especialidade definida, avaliações públicas. Você escolhe quem revisa.',
              },
              {
                icon: Lock,
                title: 'Pagamento seguro com escrow',
                body: 'Seu dinheiro fica retido até você aprovar a entrega. Split automático com a plataforma.',
              },
              {
                icon: Clock,
                title: 'Prazo definido upfront',
                body: 'O advogado te diz quanto tempo vai levar. Atrasou? Você é avisado e pode cancelar.',
              },
              {
                icon: FileSearch,
                title: 'Histórico de versões',
                body: 'Original, revisão, contrarrevisão — tudo versionado, com diff visual entre versões.',
              },
              {
                icon: Shield,
                title: 'Conversas no app',
                body: 'Chat em tempo real e videocall direto na plataforma, sem trocar WhatsApp ou email.',
              },
            ].map((f) => (
              <Card key={f.title}>
                <CardContent className="pt-6">
                  <f.icon className="h-5 w-5 text-accent" />
                  <h3 className="mt-4 font-serif text-xl">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground text-pretty">{f.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works (compact) */}
      <section className="container-px mx-auto max-w-7xl py-24">
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <h2 className="font-serif text-display-lg text-balance">Quatro passos. Sem reunião.</h2>
            <p className="mt-4 text-muted-foreground text-pretty">
              Tudo acontece dentro da plataforma — do upload do contrato até a aprovação final.
            </p>
            <ol className="mt-8 space-y-4">
              {[
                ['1', 'Suba o contrato', 'PDF ou DOCX, até 20MB. A IA lê em segundos.'],
                ['2', 'Veja a pré-análise', 'Cláusulas críticas, riscos e sugestões — antes de gastar um real.'],
                ['3', 'Escolha o advogado', 'Filtre por especialidade, avaliação e preço. Cada um define a própria tabela.'],
                ['4', 'Aprove e avalie', 'Pagamento liberado só quando você aprovar a entrega.'],
              ].map(([n, title, body]) => (
                <li key={n} className="flex gap-4">
                  <span className="font-serif text-2xl text-accent w-8 flex-none">{n}</span>
                  <div>
                    <h4 className="font-medium">{title}</h4>
                    <p className="text-sm text-muted-foreground">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <Card className="bg-primary text-primary-foreground border-0 overflow-hidden">
            <CardContent className="p-8">
              <Badge variant="accent" className="mb-4">EXEMPLO</Badge>
              <h3 className="font-serif text-2xl">Cláusula de não-concorrência</h3>
              <p className="text-sm text-primary-foreground/70 mt-2 mb-4">
                Identificada automaticamente como <span className="text-accent font-medium">alto risco</span>:
              </p>
              <div className="bg-background/5 rounded-md p-4 text-xs font-mono leading-relaxed text-primary-foreground/80">
                "...o CONTRATADO se compromete a não exercer atividade similar em território nacional pelo prazo de 5 (cinco) anos após o término..."
              </div>
              <ul className="mt-4 space-y-2 text-sm text-primary-foreground/80">
                <li className="flex gap-2">
                  <span className="text-accent">→</span> Prazo provavelmente abusivo (jurisprudência sugere 1-2 anos máx).
                </li>
                <li className="flex gap-2">
                  <span className="text-accent">→</span> Falta contrapartida financeira para validar a restrição.
                </li>
                <li className="flex gap-2">
                  <span className="text-accent">→</span> Território "nacional" pode ser considerado excessivo.
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* For lawyers */}
      <section className="border-t border-border/60 bg-primary text-primary-foreground">
        <div className="container-px mx-auto max-w-7xl py-24">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <Badge variant="accent" className="mb-6">Para advogados</Badge>
              <h2 className="font-serif text-display-lg text-balance">
                Cliente qualificado, contrato pré-analisado, pagamento garantido.
              </h2>
              <p className="mt-4 text-primary-foreground/70 text-pretty">
                Você define seus preços. A IA já fez o trabalho braçal de identificar cláusulas. O usuário já está pré-qualificado e quer fechar. Você foca no que importa: análise jurídica.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild variant="accent" size="lg">
                  <Link to="/cadastro/advogado">Cadastrar como advogado</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10">
                  <Link to="/como-funciona">Saiba mais</Link>
                </Button>
              </div>
            </div>
            <ul className="space-y-4">
              {[
                ['85%', 'do valor cobrado vai direto pra você (15% taxa da plataforma).'],
                ['0', 'cliente que precisa explicar o básico — eles já viram a pré-análise.'],
                ['Escrow', 'pagamento retido até você entregar. Sem calote.'],
                ['Você define', 'tabela de preços por tipo de tarefa. Sem teto, sem piso.'],
              ].map(([n, body]) => (
                <li key={n} className="flex gap-6 border-b border-primary-foreground/10 pb-4">
                  <span className="font-serif text-3xl text-accent w-32 flex-none">{n}</span>
                  <p className="text-sm text-primary-foreground/80 self-center">{body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-px mx-auto max-w-7xl py-24 text-center">
        <h2 className="font-serif text-display-lg text-balance max-w-3xl mx-auto">
          Pronto pra revisar seu próximo contrato em horas?
        </h2>
        <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-pretty">
          Suba o documento e receba a pré-análise IA sem custo. Você só paga se decidir contratar um advogado.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" variant="accent" className="gap-2">
            <Link to="/cadastro">
              Começar grátis
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/advogados">Ver advogados</Link>
          </Button>
        </div>
      </section>
    </MarketingLayout>
  );
}
